import React, { useContext } from "react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BASE_URL } from "../../../constant";
import BtnLoader from "../../BtnLoader";
import AppContext from "../../../AppContext";
import { useTranslation } from "react-i18next";
import { AlarmInfo, AlarmLimit } from "../../master/common/AlarmView";

const DevicePropertyForm = () => {
  const { t } = useTranslation();
  const { header, id, selectedId } = useParams();
  const { authCtx } = useContext(AppContext);
  const [device, setDevice] = useState();
  const [details, setDetails] = useState({
    deviceId: "",
    name: "",
    interval: "",
    sensors: [],
    alarms: [],
    active: true,
  });
  const [loading, setLoading] = useState(false);
  // const [companies, setCompanies] = useState([]);
  const navigate = useNavigate();

  const onCloseClick = () => {
    navigate(`/devicepropertylist/${header}/${id}`);
  };

  const loadProperties = async () => {
    let res = await axios.get(`${BASE_URL}/DeviceType/${id}`);
    const data = res.data;
    setDevice({
      ...data,
      sensors: data.sensors.map((d) => ({ ...d, enabled: true })),
    });
  };

  const onValueChange = (e) => {
    setDetails((prev) => {
      return { ...prev, [e.target.name]: e.target.value };
    });
  };
  const onGsmPropertiesChange = (e, i) => {
    setDetails((prev) => ({
      ...prev,
      sensors: prev.sensors.map((s, index) =>
        i === index
          ? {
              ...s,
              additionalValues: {
                ...s.additionalValues,
                [e.target.name]: e.target.value,
              },
            }
          : s
      ),
    }));
  };

  const onToggleEnable = (id) => {
    setDetails({
      ...details,
      sensors: details.sensors.map((d) =>
        d.id === id ? { ...d, enabled: !d.enabled } : d
      ),
    });
  };

  const onCheckboxChange = (e) => {
    setDetails((pre) => ({
      ...pre,
      [e.target.name]: e.target.checked,
      company:
        e.target.name === "companyAssign" && !e.target.checked
          ? null
          : pre.company,
      companyId: !e.target.checked ? null : pre.companyId,
    }));
  };

  const onSubmit = async () => {
    if (details.deviceId === null || details.deviceId === undefined) {
      toast.error("Device ID required");
    }
    if (details.name === "" || details.name === undefined) {
      toast.error("Device name required");
    } else {
      setLoading(true);
      try {
        const payload = {
          ...details,
          companyId:
            authCtx.profile.role === 100
              ? details.companyId
              : authCtx.profile.companyId,
        };
        if (!payload.id) {
          const res = await axios.post(`${BASE_URL}/api/Device`, payload);
          setDetails({ ...payload, id: res.data });
          if (res.status === 200) {
            toast.success("Device created successfully");
            navigate(`/devicepropertyform/${header}/${id}/${res.data}`);
          } else {
            toast.error("Some error occurred");
          }
        } else {
          const res = await axios.post(`${BASE_URL}/api/Device`, payload);
          if (res.status === 200) {
            toast.success("Device updated successfully");
          } else {
            toast.error("Some error occurred");
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 400) {
          const errorMessage = error.response.data;
          if (
            errorMessage.includes("deviceId") &&
            errorMessage.includes("name")
          ) {
            toast.warning(
              "Device with the same deviceId and name already exists."
            );
          } else if (errorMessage.includes("deviceId")) {
            toast.warning("Device with the same deviceId already exists.");
          } else if (errorMessage.includes("name")) {
            toast.warning("Device with the same name already exists.");
          } else {
            toast.error("Some error occurred");
          }
        }
      }
      setLoading(false);
    }
  };

  const findSensorData = (data, id) => {
    const value = data.sensors.find((sensor) => sensor.id === id);
    return value;
  };

  const loadData = async () => {
    let res = await axios.get(`${BASE_URL}/api/Device/${selectedId}`);
    const nonDefaultSensors = device.sensors.filter((s) => !s.default); // Filter out sensors with default = false
    const mappedSensors = nonDefaultSensors.map((s) => {
      const sensorData = findSensorData(res.data, s.id); // Find sensor data by ID in res.data
      return {
        ...s,
        enabled: sensorData ? sensorData.enabled : true, // Use sensorData.enabled if available, otherwise set to true
      };
    });
    const defaultSensors = res.data.sensors.filter((s) => s.default === true); // Filter out sensors with default = true
    const data = {
      ...res.data, // Spread properties of res.data
      // company: res.data.companyId
      //   ? companyData.find((x) => x.id === res.data.companyId)
      //   : null,
      interval: !res.data.interval ? device.interval : res.data.interval,
      deviceType: { name: device?.label, id: id }, // Assign id to mainDeviceId
      sensors: [...mappedSensors, ...defaultSensors], // Concatenate non-default and default sensors
    };

    setDetails(data);
  };

  // const loadLookupData = async () => {
  //   let res = await axios.get(`${BASE_URL}/api/CompanyMaster`);
  //   if (res.status === 200) {
  //     const data = res.data;
  //     setCompanies(data);
  //     return data;
  //   }
  // };

  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    const loadDatas = async () => {
      // const companyData = await loadLookupData();
      if (device !== undefined) {
        if (selectedId !== "new") {
          loadData();
        } else {
          const companyId = authCtx.profile.companyId;
          setDetails({
            ...details,
            companyId: companyId,
            // company: companyData.find((x) => x.id === companyId),
            companyAssign: companyId !== null,
            interval: device.interval,
            deviceType: { name: device?.label, id: id },
            sensors: device.sensors,
            alarms: device.alarms,
          });
        }
      }
    };
    loadDatas();
  }, [device]);

  const isValidGsmSensor = (sensor, device) => {
    if (sensor.id !== "GSM") return true;

    if (!sensor.additionalValues || !sensor.additionalValues.primaryNumber)
      return false;

    const simType = device?.defaultSensors
      ?.find((ds) => ds.name === "GSM")
      ?.additionalValues?.find((av) => av.name === "simType")?.value;

    return simType !== "Dual" || sensor.additionalValues.secondaryNumber;
  };

  // Helper function to check if all required fields are filled
  const isFormValid = (loading, details, device) => {
    return (
      !loading &&
      details.deviceId !== "" &&
      details.name !== "" &&
      details.sensors.every((s) => isValidGsmSensor(s, device))
    );
  };

  // const handleCompanyChange = (e) => {
  //   setDetails((pre) => ({ ...pre, company: e.value, companyId: e.value.id }));
  // };

  return (
    <div className="flex flex-col relative text-white pl-14 py-3 dark:bg-darkModeBg bg-lightModeBg">
      <div className=" text-xs font-medium py-4 gap-3 pl-10 h-[80vh] overflow-y-scroll">
        <div className="flex flex-col mb-4 w-fit">
          <div className="flex gap-3 justify-between">
            <div className="flex gap-2">
              <div className="flex dark:text-white text-labels">
                <button type="button" onClick={onCloseClick}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2.0"
                    stroke="currentColor"
                    className="w-7 h-7"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
                    />
                  </svg>
                </button>
              </div>
              <div className="flex">
                <label className=" font-bold text-base lg:text-xl dark:text-white text-labels">
                  {device?.label}
                  <span className="ml-1 text-sm lg:text-base dark:text-white text-labels">
                    ({device?.manufacturer})
                  </span>
                </label>
              </div>
            </div>
          </div>
          <label className=" font-semibold text-sm lg:text-base mt-3 dark:text-white text-labels">
            {t("masters.fields.detailDeviceId")}
            <span className=" text-red-600 ml-1">*</span>
          </label>
          <input
            type="text"
            className="block dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light rounded-lg mb-4 dark:bg-zinc-750 bg-inputFields dark:text-white text-inputFieldsTxts px-5 w-[20rem] lg:w-[40rem] h-9 mt-3"
            onChange={onValueChange}
            value={details?.deviceId}
            placeholder="Device ID"
            name="deviceId"
          ></input>
          <label className=" font-semibold text-sm lg:text-base dark:text-white text-labels">
            {t("masters.fields.name")}
            <span className=" text-red-600 ml-1">*</span>
          </label>
          <input
            type="text"
            className="block dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light rounded-lg mb-4 dark:bg-zinc-750 bg-inputFields dark:text-white text-inputFieldsTxts px-5 w-[20rem] lg:w-[40rem] h-9 mt-3"
            onChange={onValueChange}
            value={details?.name}
            placeholder="Name"
            name="name"
          ></input>
          <label className="text-sm lg:text-base dark:text-white text-labels">
            {t("masters.fields.dataInterval")}
          </label>
          <div className=" w-40 lg:w-80 interval-input-container">
            {/* <ComboBox
              data={intervalData}
              placeholder="Interval"
              name="interval"
              suggest={true}
              clearButton={false}
              style={{
                fontSize: "12px",
                fontWeight: "500",
                paddingLeft: "16px",
              }}
              className="block border-grey-light text-white rounded-lg dark:bg-zinc-750 bg-inputFields mb-4 w-44 h-9 px-4 lg:px-5"
              onChange={onValueChange}
              value={details?.interval}
            /> */}
            <input
              type="number"
              placeholder="Interval"
              name="interval"
              className="block border-grey-light dark:text-white text-inputFieldsTxts rounded-lg dark:bg-zinc-750 bg-inputFields mb-4 w-[20rem] lg:w-[40rem] h-9 mt-3"
              style={{
                fontSize: "12px",
                fontWeight: "500",
                paddingLeft: "16px",
                WebkitAppearance: "none",
              }}
              onChange={onValueChange}
              value={details?.interval}
            />
          </div>
          {/* {device?.gsm === true && <div className="text-xs lg:text-sm font-medium mb-2">GSM</div>} */}
          <div className="font-semibold text-base lg:text-lg py-2 dark:text-white text-black">
            {t("masters.headerTexts.sSensors")}
          </div>
          {details?.sensors !== null &&
            details?.sensors?.filter((s) => s.default).length > 0 && (
              <div className="flex gap-36">
                <label className="dark:text-white text-black text-xs lg:text-sm font-medium py-2">
                  {t("masters.fields.sensorId")}
                </label>
                <label className="dark:text-white text-black text-xs lg:text-sm font-medium py-2">
                  {t("masters.fields.name")}
                </label>
                <label className="dark:text-white text-black text-xs lg:text-sm font-medium py-2 ml-4 lg:ml-16">
                  {t("masters.fields.type")}
                </label>
              </div>
            )}
          {details?.sensors?.length > 0 &&
            details?.sensors.map(
              (item, index) =>
                item.default && (
                  <div className="flex flex-col" key={index}>
                    <div className="flex gap-3" id={item.id}>
                      <div className="flex flex-col" id="propMultiSelect">
                        <label className="flex items-center dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light text-xs lg:text-sm rounded-lg mb-4 dark:bg-zinc-750 bg-inputFields dark:text-white text-inputFieldsTxts px-4 lg:px-5 w-40 lg:w-48 h-9">
                          {item.sensorId}
                        </label>
                      </div>
                      <div className="flex flex-col">
                        <label className="flex items-center dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light text-xs lg:text-sm rounded-lg mb-4 dark:bg-zinc-750 bg-inputFields dark:text-white text-inputFieldsTxts px-5 w-48 lg:w-60 h-9">
                          {item.name}
                        </label>
                      </div>
                      <div className="flex flex-col" id="propTypecombo">
                        <label className="flex items-center dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light text-xs lg:text-sm rounded-lg mb-4 dark:bg-zinc-750 bg-inputFields dark:text-white text-inputFieldsTxts px-4 lg:px-5 w-40 lg:w-48 h-9">
                          {item.type}
                        </label>
                      </div>
                      <div className="flex flex-col" id="propTypecombo">
                        <label className="flex items-center dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts rounded-lg mb-4 dark:text-white text-inputFieldsTxts px-5">
                          <div
                            className={`inline-flex rounded-xl group mt-1 h-8 border-2 ${
                              item.enabled === true
                                ? "bg-lime-700 border-lime-700"
                                : "bg-lime-700 border-lime-700"
                            }`}
                          >
                            <button
                              type="button"
                              className={`text-xs font-medium text-center rounded-xl w-1/2 px-2 ${
                                item.enabled === true
                                  ? "bg-lime-700 text-white"
                                  : "text-white bg-white"
                              }`}
                              onClick={() => onToggleEnable(item.id)}
                            >
                              {t("masters.labels.enabled")}
                            </button>
                            <button
                              type="button"
                              className={`text-xs font-medium text-center rounded-xl w-1/2 px-2 ${
                                item.enabled === true
                                  ? "text-white bg-white"
                                  : "bg-lime-700 text-white"
                              }`}
                              onClick={() => onToggleEnable(item.id)}
                            >
                              {t("masters.labels.disabled")}
                            </button>
                          </div>
                        </label>
                      </div>
                    </div>
                    {item.id === "GSM" && (
                      <div className="flex flex-col ml-10 mb-1 w-[40rem] ">
                        <div className="flex gap-3">
                          <div className="flex  flex-col gap-3 py-1">
                            <label className="dark:text-white text-labels text-xxs lg:text-sm font-medium">
                              {t("masters.fields.primeNo")}
                              <span className="text-red-600 ml-1">*</span>
                            </label>
                            <input
                              type="text"
                              name="primaryNumber"
                              placeholder="Primary SIM"
                              className="block dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light rounded-lg dark:bg-zinc-750 bg-inputFields dark:text-white text-inputFieldsTxts px-5 h-9 "
                              value={item?.additionalValues?.primaryNumber}
                              onChange={(e) => onGsmPropertiesChange(e, index)}
                            />
                          </div>
                          {device?.defaultSensors
                            .find((ds) => ds.name === "GSM")
                            ?.additionalValues.find(
                              (av) => av.name === "simType"
                            )?.value === "Dual" && (
                            <div className="flex flex-col gap-5 py-1">
                              <label className="dark:text-white text-labels text-xxs lg:text-sm font-medium">
                                {t("masters.fields.secondaryNo")}
                                <span className="text-red-600 ml-1">*</span>
                              </label>
                              <input
                                type="text"
                                name="secondaryNumber"
                                placeholder="Secondary SIM"
                                className="block dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light rounded-lg mb-4 dark:bg-zinc-750 bg-inputFields dark:text-white text-inputFieldsTxts px-5 h-9"
                                value={item?.additionalValues?.secondaryNumber}
                                onChange={(e) =>
                                  onGsmPropertiesChange(e, index)
                                }
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col ml-10 mb-5">
                      {details.alarms &&
                        details.alarms.length > 0 &&
                        details.alarms.some(
                          (a) => a.parameterId === item.id
                        ) && (
                          <div className="flex gap-36">
                            <label className="dark:text-white text-labels font-medium text-xs lg:text-sm py-1">
                              {t("masters.headerTexts.alarms")}
                            </label>
                          </div>
                        )}
                      {details.alarms &&
                        details.alarms.map(
                          (i, index) =>
                            i.parameterId === item.id && (
                              <div className="flex flex-col py-1" key={index}>
                                <div class=" dark:bg-neutral-800 bg-zinc-500 rounded-lg w-11/12 lg:w-fit h-fit px-3 py-2">
                                  <div className="flex gap-6 ml-1 pt-2">
                                    <div className="flex flex-col gap-3 text-xs lg:text-sm py-1 w-40 pr-3 border-r-2 border-zinc-700">
                                      <div className="flex w-24">
                                        <label className="text-white text-xs lg:text-sm font-medium">
                                          {t("masters.fields.alrmName")}
                                        </label>
                                      </div>
                                      <div className="flex w-36">
                                        <label>{i.alarmName}</label>
                                      </div>
                                    </div>
                                    {/* Alarm Info */}
                                    {i.infoEnabled === true &&
                                      (i.parameterType === "String" ||
                                      i.parameterType === "Boolean" ? (
                                        <AlarmInfo
                                          label={t("masters.fields.info")}
                                          value={i.infoSensorValue}
                                          text={i.infoAlarmText}
                                          t={t}
                                        />
                                      ) : i.parameterType === "Number" ? (
                                        <AlarmLimit
                                          label={t("masters.fields.info")}
                                          min={i.infoLowerLimit}
                                          max={i.infoUpperLimit}
                                          t={t}
                                        />
                                      ) : null)}

                                    {/* Warning Info */}
                                    {i.warningEnabled === true &&
                                      (i.parameterType === "String" ||
                                      i.parameterType === "Boolean" ? (
                                        <AlarmInfo
                                          label={t("masters.fields.warning")}
                                          value={i.warningSensorValue}
                                          text={i.warningAlarmText}
                                          t={t}
                                        />
                                      ) : i.parameterType === "Number" ? (
                                        <AlarmLimit
                                          label={t("masters.fields.warning")}
                                          min={i.warningLowerLimit}
                                          max={i.warningUpperLimit}
                                          t={t}
                                        />
                                      ) : null)}

                                    {/* Critical Info */}
                                    {i.criticalEnabled === true &&
                                      (i.parameterType === "String" ||
                                      i.parameterType === "Boolean" ? (
                                        <AlarmInfo
                                          label={t("masters.fields.critical")}
                                          value={i.criticalSensorValue}
                                          text={i.criticalAlarmText}
                                          t={t}
                                        />
                                      ) : i.parameterType === "Number" ? (
                                        <AlarmLimit
                                          label={t("masters.fields.critical")}
                                          min={i.criticalLowerLimit}
                                          max={i.criticalUpperLimit}
                                          t={t}
                                        />
                                      ) : null)}
                                  </div>
                                </div>
                              </div>
                            )
                        )}
                    </div>
                  </div>
                )
            )}
        </div>
        <div className="font-semibold text-base lg:text-lg dark:text-white text-labels">
          {t("masters.headerTexts.sensors")}
        </div>
        <div className="flex flex-col gap-3 py-2">
          {details?.sensors !== null &&
            details?.sensors?.filter((s) => !s.default).length > 0 && (
              <div className="flex gap-36">
                <label className="dark:text-white text-labels text-xs lg:text-sm font-medium py-2">
                  {t("masters.fields.sensorId")}
                </label>
                <label className="dark:text-white text-labels text-xs lg:text-sm font-medium py-2">
                  {t("masters.fields.name")}
                </label>
                <label className="dark:text-white text-labels text-xs lg:text-sm font-medium py-2 ml-2 lg:ml-16">
                  {t("masters.fields.type")}
                </label>
                <label className="text-white text-xs lg:text-sm font-medium py-2 ml-4 lg:ml-16">
                  {t("masters.fields.action")}
                </label>
              </div>
            )}
          {details?.sensors?.length > 0 &&
            details?.sensors.map(
              (item) =>
                !item.default && (
                  <div className="flex flex-col gap-3" key={item.id}>
                    <div className="flex gap-3" id={item.id}>
                      <div className="flex flex-col" id="propMultiSelect">
                        <label className="flex items-center dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light text-xs lg:text-sm rounded-lg mb-4 dark:bg-zinc-750 bg-inputFields dark:text-white text-inputFieldsTxts px-4 lg:px-5 w-40 lg:w-48 h-9">
                          {item.sensorId}
                        </label>
                      </div>
                      <div className="flex flex-col">
                        <label className="flex items-center dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light text-xs lg:text-sm rounded-lg mb-4 dark:bg-zinc-750 bg-inputFields dark:text-white text-inputFieldsTxts px-5 w-48 lg:w-60 h-9">
                          {item.name}
                        </label>
                      </div>
                      <div className="flex flex-col" id="propTypecombo">
                        <label className="flex items-center dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light text-xs lg:text-sm rounded-lg mb-4 dark:bg-zinc-750 bg-inputFields dark:text-white text-inputFieldsTxts px-4 lg:px-5 w-40 lg:w-48 h-9">
                          {item.type}
                        </label>
                      </div>
                      <div className="flex flex-col" id="propTypecombo">
                        <label className="flex items-center dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts rounded-lg mb-4 dark:text-white text-inputFieldsTxts px-5">
                          <div
                            className={`inline-flex rounded-xl group mt-1 h-8 border-2 ${
                              item.enabled === true
                                ? "bg-lime-700 border-lime-700"
                                : "bg-lime-700 border-lime-700"
                            }`}
                          >
                            <button
                              type="button"
                              className={`text-xs font-medium text-center rounded-xl w-1/2 px-2 ${
                                item.enabled === true
                                  ? "bg-lime-700 text-white"
                                  : "text-white bg-white"
                              }`}
                              onClick={() => onToggleEnable(item.id)}
                            >
                              {t("masters.labels.enabled")}
                            </button>
                            <button
                              type="button"
                              className={`text-xs font-medium text-center rounded-xl w-1/2 px-2 ${
                                item.enabled === true
                                  ? "text-white bg-white"
                                  : "bg-lime-700 text-white"
                              }`}
                              onClick={() => onToggleEnable(item.id)}
                            >
                              {t("masters.labels.disabled")}
                            </button>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>
                )
            )}
        </div>
      </div>
      <div className="flex justify-center items-center sticky bg-inherit w-full bottom-7 gap-3 ">
        <div className="flex justify-center items-center mt-3 gap-3 mr-72 pl-4">
          <div className="flex justify-center form-check form-check-inline mr-1">
            <label
              className="form-check-label inline-block text-xs lg:text-sm dark:text-white text-labels mr-3 mt-1"
              forhtml="inlineCheckbox1"
            >
              {t("masters.fields.active")}
            </label>
            <input
              name="active"
              type="checkbox"
              className="focus:ring-blue-500 h-4 lg:h-5 w-4 lg:w-5 text-blue-600 border-gray-300 rounded mt-1"
              id="inlineCheckbox1"
              defaultChecked={true}
              onChange={onCheckboxChange}
              checked={details?.active}
              value={details?.active}
            ></input>
          </div>
          <button
            type="button"
            name="create"
            onClick={onCloseClick}
            className=" dark:bg-white bg-closeBtns dark:text-black text-closeBtnTxts text-xs lg:text-sm rounded-4xl px-10 lg:w-48 h-10"
          >
            {t("button.close")}
          </button>
          <button
            type="button"
            name="create"
            onClick={onSubmit}
            className="dark:bg-indigo-700 bg-submitBtns dark:text-black text-submitBtnTxts text-xs lg:text-sm rounded-4xl px-10 lg:w-48 h-10 flex justify-center items-center gap-3 disabled:opacity-40"
            disabled={!isFormValid(loading, details, device)}
          >
            {loading.formLoading && <BtnLoader />}
            {details?.id ? t("button.update") : t("button.create")}
          </button>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <ToastContainer />
    </div>
  );
};

export default DevicePropertyForm;
